import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { DateRangePicker } from '@creditornot/cb-date-picker';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuItemGroup } from '@creditornot/cb-menu';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PredefinedDateRangeOptions, telemetryClickDatePickerInHomeInsightPage } from '../telemetry';
import { DateRange } from '../types';
import { getDateRanges } from '../utils';

export type Props = {
  onChange: (dateRange: DateRange) => void;
  value: DateRange;
  locale?: Locale | string;
};

const Root = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 284px;
  padding: 8px;
  width: 100%;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
`;

const DataDelayAlert = styled(Alert)`
  margin-block-start: 12px;
`;

const FilterDateRangePicker = ({ onChange, value, locale }: Props) => {
  const {
    todayDateRange,
    yesterdayDateRange,
    lastWeekDateRange,
    thisMonthDateRange,
    last28daysDateRange,
    disabledDays,
  } = getDateRanges();

  const shouldShowDataDelayNotice =
    value && value.from && value.to && DateTime.now().startOf('day') <= DateTime.fromJSDate(value.to);

  return (
    <Root>
      <DateRangePicker onChange={onChange} value={value} disabledDays={disabledDays} locale={locale} />
      <Divider />
      <MenuItemGroup>
        <Button stretch variant="lightBlue" size="small" onClick={() => onChange(todayDateRange)}>
          <FormattedMessage id="purchases.labels.today" />
        </Button>
        <Button
          stretch
          variant="lightBlue"
          size="small"
          onClick={() => {
            telemetryClickDatePickerInHomeInsightPage(PredefinedDateRangeOptions.DATE_PICKER_YESTERDAY);
            onChange(yesterdayDateRange);
          }}
        >
          <FormattedMessage id="purchases.labels.yesterday" />
        </Button>
        <Button
          stretch
          variant="lightBlue"
          size="small"
          onClick={() => {
            telemetryClickDatePickerInHomeInsightPage(PredefinedDateRangeOptions.DATE_PICKER_LAST_WEEK);
            onChange(lastWeekDateRange);
          }}
        >
          <FormattedMessage id="purchases.labels.last-week" />
        </Button>
        <Button
          stretch
          variant="lightBlue"
          size="small"
          onClick={() => {
            telemetryClickDatePickerInHomeInsightPage(PredefinedDateRangeOptions.DATE_PICKER_THIS_MONTH);
            onChange(thisMonthDateRange);
          }}
        >
          <FormattedMessage id="purchases.labels.this-month" />
        </Button>
        <Button
          stretch
          variant="lightBlue"
          size="small"
          onClick={() => {
            telemetryClickDatePickerInHomeInsightPage(PredefinedDateRangeOptions.DATE_PICKER_LAST_28_DAYS);
            onChange(last28daysDateRange);
          }}
        >
          <FormattedMessage id="insights.date-picker.last-28" />
        </Button>
      </MenuItemGroup>
      {shouldShowDataDelayNotice && (
        <DataDelayAlert variant="error">
          <FormattedMessage id="insights.data-delay" />
        </DataDelayAlert>
      )}
    </Root>
  );
};

export default FilterDateRangePicker;
